/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiSettings, FiLink, FiUser, FiSave } from "react-icons/fi";
import WhatsLayout from "../layouts/WhatsLayout";
import { useUI } from "../theme/ThemeProvider";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";

// Using colors from useUI instead of hardcoded COLORS

export default function AdminStoreSettings() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);

  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentTenantData, setCurrentTenantData] = useState(null);

  const [general, setGeneral] = useState({
    storeName: "",
    storeSlug: "",
  });
  const storeUrl = React.useMemo(() => {
    if (!general.storeSlug) return "";
    return `https://${general.storeSlug}.madar.iq/`;
  }, [general.storeSlug]);

  const [social, setSocial] = useState({
    facebook: "",
    instagram: "",
    snapchat: "",
    tiktok: "",
    whatsapp: "",
  });

  const [policies, setPolicies] = useState({
    privacy: "",
    delivery: "",
    refund: "",
  });

  // Fetch tenant data on component mount
  useEffect(() => {
    fetchTenantData();
  }, []);

  const fetchTenantData = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("authToken");

      // 🔥 إضافة tenantId إلى الـ API مباشرة
      const res = await fetch(
        `${baseUrl}/GetAllTenantFullDetails?tenantId=${tenantId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      if (!res.ok) {
        throw new Error("Failed to fetch tenant data");
      }

      const data = await res.json();
      console.log("📥 Tenant details:", data);
      const tenant = Array.isArray(data) ? data[0] : data;

      if (!tenant) return;

      // general
      setGeneral({
        storeName:
          tenant.ClientName ||
          tenant.clientName ||
          tenant.TenantName ||
          tenant.tenantName ||
          "",
        storeSlug: tenant.StoreSlug || tenant.storeSlug || "",
      });

      // social
      setSocial({
        facebook: tenant.facebook || tenant.Facebook || "",
        instagram: tenant.instagram || tenant.Instagram || "",
        snapchat: tenant.snapchat || tenant.Snapchat || "",
        tiktok: tenant.tiktok || tenant.Tiktok || "",
        whatsapp: tenant.whatsapp || tenant.Whatsapp || "",
      });

      // policies
      setPolicies({
        privacy: tenant.privacyPolicy || tenant.PrivacyPolicy || "",
        delivery: tenant.deliveryPolicy || tenant.DeliveryPolicy || "",
        refund: tenant.refundPolicy || tenant.RefundPolicy || "",
      });

      setCurrentTenantData(tenant);
      console.log("⭐ Final parsed data:", {
        general: {
          storeName:
            tenant.ClientName ||
            tenant.clientName ||
            tenant.TenantName ||
            tenant.tenantName ||
            "",
          storeSlug: tenant.StoreSlug || tenant.storeSlug || "",
        },
        social: {
          facebook: tenant.facebook || tenant.Facebook || "",
          instagram: tenant.instagram || tenant.Instagram || "",
          snapchat: tenant.snapchat || tenant.Snapchat || "",
          tiktok: tenant.tiktok || tenant.Tiktok || "",
          whatsapp: tenant.whatsapp || tenant.Whatsapp || "",
        },
        policies: {
          privacy: tenant.privacyPolicy || tenant.PrivacyPolicy || "",
          delivery: tenant.deliveryPolicy || tenant.DeliveryPolicy || "",
          refund: tenant.refundPolicy || tenant.RefundPolicy || "",
        },
      });

    } catch (err) {
      console.error("❌ Error fetching tenant data:", err);
    } finally {
      setLoading(false);
    }
  };


  const handleSave = async () => {
    if (!perms.editStoreSettings) return;

    if (!currentTenantData || tenantId <= 0) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ" : "Error",
        text: isArabic ? "بيانات المتجر غير موجودة" : "Tenant data not found",
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
        confirmButtonColor: colors.mainColor,
      });
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem("authToken");

      // Validate Slug Uniqueness
      try {
        const validationRes = await fetch(`${baseUrl}/GetAllTenantFullDetails?tenantId=-1`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });
        if (validationRes.ok) {
          const allTenants = await validationRes.json();
          const existing = allTenants.find(t =>
            t.TenantId !== tenantId &&
            ((t.storeSlug && t.storeSlug.toLowerCase() === general.storeSlug.trim().toLowerCase()) ||
              (t.StoreSlug && t.StoreSlug.toLowerCase() === general.storeSlug.trim().toLowerCase()))
          );
          if (existing) {
            setSaving(false);
            Swal.fire({
              icon: "error",
              title: isArabic ? "اسم مستخدم" : "Name Used",
              text: isArabic ? "اسم الرابط (Slug) مستخدم مسبقاً، يرجى اختيار اسم آخر" : "This store slug is already used by another store, please choose another one",
              background: isDark ? "#1E293B" : "#fff",
              color: isDark ? "#fff" : "#000",
              confirmButtonColor: colors.mainColor,
            });
            return;
          }
        }
      } catch (err) {
        console.error("Failed to validate slug", err);
      }

      const tenant = currentTenantData;

      // 🔥 FormData
      const formData = new FormData();

      formData.append("TenantId", tenantId);
      formData.append("TenantName", general.storeName.trim());
      formData.append("EngTenantName", tenant.EngTenantName || "");
      formData.append("Email", tenant.Email || "");
      formData.append("Phone", tenant.Phone || "");
      formData.append("SubDomain", tenant.SubDomain || "");
      formData.append("IsActive", tenant.IsActive ?? 1);
      formData.append("CreatedAt", tenant.CreatedAt || "");
      formData.append("Product", tenant.Product || "");
      formData.append("MainPlanId", tenant.MainPlanId || 0);
      formData.append("TenantDtlId", tenant.TenantDtlId || 0);

      // LogoFile (optional)
      if (tenant.LogoFile instanceof File) {
        formData.append("LogoFile", tenant.LogoFile);
      } else {
        formData.append("LogoFile", "");
      }

      // Basic details
      formData.append("ClientName", general.storeName.trim());
      formData.append("SectorType", tenant.SectorType || "");
      formData.append("BusinessName", tenant.BusinessName || "");
      formData.append("CompanyName", tenant.CompanyName || "");
      formData.append("ThemeId", tenant.ThemeId || 0);
      formData.append("DetailPlanId", tenant.DetailPlanId || 0);

      formData.append("ColorName", tenant.ColorName || "");
      formData.append("PlanType", tenant.PlanType || "");
      formData.append("EngPlanType", tenant.EngPlanType || "");
      formData.append("Price", tenant.Price || 0);
      formData.append("Descripsion", tenant.Descripsion || "");
      formData.append("EngDescripsion", tenant.EngDescripsion || "");

      // Notes
      for (let i = 1; i <= 8; i++) {
        formData.append(`Note${i}`, tenant[`Note${i}`] || "");
      }

      // Payment Methods
      const pm = tenant.PaymentMethods || [];
      pm.forEach((p, index) => {
        formData.append(`PaymentMethods[${index}].paymentMethodId`, p.paymentMethodId || 0);
        formData.append(`PaymentMethods[${index}].paymentMethodName`, p.paymentMethodName || "");
        formData.append(`PaymentMethods[${index}].engPaymentMethodName`, p.engPaymentMethodName || "");
        formData.append(`PaymentMethods[${index}].isActive`, p.isActive ?? 1);
      });

      // Social
      formData.append("Facebook", social.facebook.trim());
      formData.append("Instagram", social.instagram.trim());
      formData.append("Snapchat", social.snapchat.trim());
      formData.append("Tiktok", social.tiktok.trim());
      formData.append("Whatsapp", social.whatsapp.trim());

      // Policies
      formData.append("PrivacyPolicy", policies.privacy.trim());
      formData.append("DeliveryPolicy", policies.delivery.trim());
      formData.append("RefundPolicy", policies.refund.trim());

      // Slug
      formData.append("StoreSlug", general.storeSlug.trim());

      console.log("📤 Sending UpdateTenantFullDetails...");

      const res = await fetch(`${baseUrl}/UpdateTenantDetails/UpdateTenantFullDetails`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      const result = await res.json();
      console.log("📥 Update Response:", result);

      if (!res.ok) {
        throw new Error(result.message || "Failed to update tenant");
      }
      await fetchTenantData();

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم التحديث بنجاح" : "Updated Successfully",
        timer: 2000,
        showConfirmButton: false,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } catch (err) {
      console.error("❌ Update Error:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل التحديث" : "Update Failed",
        text: err.message,
      });
    } finally {
      setSaving(false);
    }
  };


  if (!perms.viewStoreSettings) {
    return <NoAccess />;
  }
  return (
    <WhatsLayout title={isArabic ? "إعدادات المتجر" : "Store Settings"}>
      <section
        dir={isArabic ? "rtl" : "ltr"}
        className={`min-h-screen px-5 sm:px-10 py-10 transition-all duration-300 ${isDark ? "text-gray-100" : "text-gray-900"
          }`}
        style={{
          background: isDark
            ? "linear-gradient(180deg, #0B1020 0%, #131827 100%)"
            : "linear-gradient(180deg, #F8FAFC 0%, #E2E8F0 100%)",
        }}
      >


        {/* ===== تبويبات التنقل ===== */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {[
            perms.viewStoreSettings && { key: "general", icon: <FiUser />, label: isArabic ? "عام" : "General" },
            perms.viewStoreSettings && { key: "social", icon: <FiLink />, label: isArabic ? "التواصل الاجتماعي" : "Social Links" },
            perms.viewStoreSettings && { key: "policies", icon: <FiSettings />, label: isArabic ? "السياسات" : "Policies" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-2 rounded-xl font-semibold transition-all shadow-md ${activeTab === tab.key
                ? "text-white scale-[1.05]"
                : isDark
                  ? "text-gray-300"
                  : "text-gray-600"
                }`}
              style={{
                background: activeTab === tab.key
                  ? colors.mainColor
                  : isDark
                    ? "#1E293B"
                    : "#ffffff",
                border: isDark
                  ? "1px solid rgba(255,255,255,0.25)"
                  : "1px solid rgba(0,0,0,0.1)",
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#7400BF]"></div>
              <p className="mt-4" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                {isArabic ? "جاري التحميل..." : "Loading..."}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* ===== محتوى التبويبات ===== */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="rounded-2xl shadow-lg border p-6"
              style={{
                backgroundColor: isDark ? "#1E293B" : "#ffffff",
                border: isDark
                  ? "1px solid rgba(255,255,255,0.25)"
                  : "1px solid rgba(0,0,0,0.1)",
              }}
            >
              {/* تبويب عام */}
              {activeTab === "general" && perms.editStoreSettings && (
                <div>
                  <h3
                    className="text-xl font-bold mb-5"
                    style={{ color: colors.mainColor }}
                  >
                    {isArabic ? "الإعدادات العامة" : "General Settings"}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        className="block mb-2 font-semibold"
                        style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}
                      >
                        {isArabic ? "اسم المتجر" : "Store Name"}
                      </label>
                      <input
                        type="text"
                        value={general.storeName}
                        onChange={(e) => {
                          const value = e.target.value
                            .toLowerCase()          // منع الأحرف الكبيرة
                            .replace(/[^a-z0-9-]/g, "") // حذف أي رمز غير مسموح
                            .replace(/--+/g, "-"); // منع تكرار الشرطات

                          setGeneral({ ...general, storeSlug: value });
                        }}
                        className="w-full rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400 transition-colors"
                        style={{
                          backgroundColor: isDark ? "#0F172A" : "#F8FAFC",
                          border: isDark
                            ? "1px solid rgba(255,255,255,0.25)"
                            : "1px solid rgba(0,0,0,0.1)",
                          color: isDark ? "#f1f5f9" : "#1e293b",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        className="block mb-2 font-semibold"
                        style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}
                      >
                        {isArabic ? "الاسم في الرابط" : "Store Slug"}
                      </label>
                      <input
                        type="text"
                        value={general.storeSlug}
                        onChange={(e) => setGeneral({ ...general, storeSlug: e.target.value })}
                        className="w-full rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400 transition-colors"
                        style={{
                          backgroundColor: isDark ? "#0F172A" : "#F8FAFC",
                          border: isDark
                            ? "1px solid rgba(255,255,255,0.25)"
                            : "1px solid rgba(0,0,0,0.1)",
                          color: isDark ? "#f1f5f9" : "#1e293b",
                        }}
                      />
                      {storeUrl && (
                        <div className="mt-3 flex items-center justify-between gap-3 p-3 rounded-lg text-sm"
                          style={{
                            background: isDark ? "#0F172A" : "#F1F5F9",
                            border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #CBD5E1",
                            color: isDark ? "#E5E7EB" : "#1E293B"
                          }}>

                          <div className="truncate">
                            <span className="opacity-70">{isArabic ? "رابط المتجر:" : "Store URL:"}</span><br />
                            <a href={storeUrl} target="_blank" rel="noreferrer"
                              className="font-semibold text-purple-400 ">
                              {storeUrl}
                            </a>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(storeUrl);
                                Swal.fire({
                                  icon: "success",
                                  title: isArabic ? "تم النسخ" : "Copied",
                                  timer: 1500,
                                  showConfirmButton: false,
                                });
                              }}
                              className="p-2 rounded-md transition"
                              style={{ background: colors.mainColor }}
                            >
                              <FiLink className="text-white" />
                            </button>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>

                  <div className="flex justify-center sm:justify-end">
                    <button
                      onClick={perms.editStoreSettings ? handleSave : null}
                      disabled={saving}
                      className="mt-6 flex items-center gap-2 px-6 py-2 rounded-lg text-white font-semibold shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: saving ? "#94a3b8" : colors.mainColor }}
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          {isArabic ? "جاري الحفظ..." : "Saving..."}
                        </>
                      ) : (
                        <>
                          <FiSave /> {isArabic ? "حفظ التعديلات" : "Save Changes"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* تبويب التواصل الاجتماعي */}
              {activeTab === "social" && perms.editStoreSettings && (
                <div>
                  <h3
                    className="text-xl font-bold mb-5"
                    style={{ color: colors.mainColor }}
                  >
                    {isArabic ? "وسائل التواصل الاجتماعي" : "Social Media Links"}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {Object.keys(social).map((key) => (
                      <div key={key}>
                        <label
                          className="block mb-2 font-semibold capitalize"
                          style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}
                        >
                          {isArabic
                            ? {
                              facebook: "رابط الفيسبوك",
                              instagram: "رابط الانستغرام",
                              snapchat: "رابط السناب شات",
                              tiktok: "رابط التيك توك",
                              whatsapp: "رابط الواتساب",
                            }[key]
                            : `${key.charAt(0).toUpperCase() + key.slice(1)} Link`}
                        </label>
                        <input
                          type="text"
                          value={social[key]}
                          onChange={(e) => setSocial({ ...social, [key]: e.target.value })}
                          className="w-full rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400 transition-colors"
                          style={{
                            backgroundColor: isDark ? "#0F172A" : "#F8FAFC",
                            border: isDark
                              ? "1px solid rgba(255,255,255,0.25)"
                              : "1px solid rgba(0,0,0,0.1)",
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-center sm:justify-end">
                    <button
                      onClick={perms.editStoreSettings ? handleSave : null}
                      disabled={saving}
                      className="mt-6 flex items-center gap-2 px-6 py-2 rounded-lg text-white font-semibold shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: saving ? "#94a3b8" : colors.mainColor }}
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          {isArabic ? "جاري الحفظ..." : "Saving..."}
                        </>
                      ) : (
                        <>
                          <FiSave /> {isArabic ? "حفظ التعديلات" : "Save Changes"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* تبويب السياسات */}
              {activeTab === "policies" && perms.editStoreSettings && (
                <div>
                  <h3
                    className="text-xl font-bold mb-5"
                    style={{ color: colors.mainColor }}
                  >
                    {isArabic ? "سياسات المتجر" : "Store Policies"}
                  </h3>
                  <div className="grid grid-cols-1 gap-5">
                    {Object.keys(policies).map((key) => (
                      <div key={key}>
                        <label
                          className="block mb-2 font-semibold"
                          style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}
                        >
                          {isArabic
                            ? {
                              privacy: "سياسة الخصوصية",
                              delivery: "سياسة التوصيل",
                              refund: "سياسة الاسترجاع",
                            }[key]
                            : `${key.charAt(0).toUpperCase() + key.slice(1)} Policy`}
                        </label>
                        <textarea
                          rows="4"
                          value={policies[key]}
                          onChange={(e) => setPolicies({ ...policies, [key]: e.target.value })}
                          className="w-full rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400 transition-colors resize-none"
                          style={{
                            backgroundColor: isDark ? "#0F172A" : "#F8FAFC",
                            border: isDark
                              ? "1px solid rgba(255,255,255,0.25)"
                              : "1px solid rgba(0,0,0,0.1)",
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-center sm:justify-end">
                    <button
                      onClick={perms.editStoreSettings ? handleSave : null}
                      disabled={saving}
                      className="mt-6 flex items-center gap-2 px-6 py-2 rounded-lg text-white font-semibold shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: saving ? "#94a3b8" : colors.mainColor }}
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          {isArabic ? "جاري الحفظ..." : "Saving..."}
                        </>
                      ) : (
                        <>
                          <FiSave /> {isArabic ? "حفظ التعديلات" : "Save Changes"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </section>
    </WhatsLayout>
  );
}
